import { Icon, spacing, Text } from '@scality/core-ui';
import type { JSONSchema7 } from 'json-schema';
import styled from 'styled-components';
import type { AIToolComponent } from '../../chat-messages/GuardianMarkdown';

/**
 * Fallback shown in place of a dashboard widget that couldn't be produced by the backend
 * (unknown/unimplemented type, unknown metric, or a resolve failure). It mirrors the
 * template `ComponentSpec` (the intended component type, metric, title) so a future PR can
 * offer a per-widget "refresh" without re-rendering the whole dashboard. Refresh is not
 * implemented here.
 */
export type ErrorWidgetParams = {
  /** The component type the backend tried to render (mirrors ComponentSpec.type). */
  componentType?: string;
  /** The metric the widget was bound to (mirrors ComponentSpec.metric_name). */
  metricName?: string;
  /** The widget's title (mirrors ComponentSpec.title). */
  title?: string;
  /** Optional human-readable reason. */
  message?: string;
};

const Fallback = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${spacing.r8};
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: 8rem;
  padding: ${spacing.r16};
  margin: ${spacing.r16};
  border: 1px dashed ${(props) => props.theme.border};
  border-radius: ${spacing.r4};
  background-color: ${(props) => props.theme.backgroundLevel2};
`;

export const ErrorWidget: AIToolComponent<ErrorWidgetParams> = ({ params }) => {
  const heading = params.title || params.componentType || 'Widget';
  return (
    <Fallback>
      <Icon name="Exclamation-triangle" color="statusWarning" size="lg" />
      <Text isEmphazed color="textPrimary">
        {heading}
      </Text>
      <Text variant="Small" color="textSecondary">
        {params.message ?? "This widget couldn't be loaded."}
      </Text>
    </Fallback>
  );
};

ErrorWidget.description =
  'Internal fallback rendered when a dashboard widget cannot be produced. Not emitted by the agent.';

ErrorWidget.schema = {
  type: 'object',
  properties: {
    componentType: { type: 'string' },
    metricName: { type: 'string' },
    title: { type: 'string' },
    message: { type: 'string' },
  },
  additionalProperties: false,
} satisfies JSONSchema7;
